
import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { businessInfo } from '@/data/businessInfo';

const formSchema = z.object({
  name: z.string().min(2, { message: "Name is required" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  service: z.string().optional(),
  propertyType: z.string().optional(),
  budget: z.string().optional(),
  timeline: z.string().optional(),
  message: z.string().min(10, { message: "Please provide some details about your project" }),
});

type FormValues = z.infer<typeof formSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      service: '',
      propertyType: '',
      budget: '',
      timeline: '',
      message: '',
    },
  });

  const onSubmit = (values: FormValues) => {
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', values);
      setIsSubmitting(false);
      setShowDialog(true);
      
      form.reset();
    }, 1500);
  };

  return (
    <section className="py-16 bg-pkstone-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-8 h-full">
              <h3 className="text-2xl font-serif font-bold mb-6 text-pkstone-900">
                Contact {businessInfo.name}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-pkearth-100 p-3 rounded-full mr-4">
                    <Phone className="h-5 w-5 text-pkearth-700" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Phone</h4>
                    <a 
                      href={`tel:${businessInfo.phoneInternational}`}
                      className="text-pkstone-600 hover:text-pkearth-700 transition-colors"
                    >
                      {businessInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-pkearth-100 p-3 rounded-full mr-4">
                    <Mail className="h-5 w-5 text-pkearth-700" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Email</h4>
                    <a 
                      href={`mailto:${businessInfo.email}`}
                      className="text-pkstone-600 hover:text-pkearth-700 transition-colors"
                    >
                      {businessInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-pkearth-100 p-3 rounded-full mr-4">
                    <MapPin className="h-5 w-5 text-pkearth-700" />
                  </div>
                  <div>
                    <h4 className="font-medium text-pkstone-900 mb-1">Office Location</h4>
                    <address className="text-pkstone-600 not-italic">
                      {businessInfo.address.street}<br />
                      {businessInfo.address.city}, {businessInfo.address.state} {businessInfo.address.zip}
                    </address>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-pkstone-100">
                <h4 className="font-medium text-pkstone-900 mb-3">Service Areas</h4>
                <p className="text-pkstone-600 mb-4">
                  Proudly serving Fairfield County including Greenwich, Stamford, 
                  Darien, Westport, Fairfield, and surrounding areas.
                </p>
              </div>
              
              <div className="mt-8 pt-8 border-t border-pkstone-100">
                <h4 className="font-medium text-pkstone-900 mb-3">Business Hours</h4>
                <ul className="text-pkstone-600">
                  <li className="flex justify-between mb-1">
                    <span>Monday - Friday</span>
                    <span>{businessInfo.hours.weekday}</span>
                  </li>
                  <li className="flex justify-between mb-1">
                    <span>Saturday</span>
                    <span>{businessInfo.hours.saturday}</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Sunday</span>
                    <span>{businessInfo.hours.sunday}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-serif font-bold mb-6 text-pkstone-900">
                Request a Consultation
              </h3>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <input
                              {...field}
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                              placeholder="John Doe"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <input
                              {...field}
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                              placeholder="john@example.com"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <input
                              {...field}
                              type="tel"
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                              placeholder="(123) 456-7890"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="service"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Service Interested In</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                            >
                              <option value="">Select a service</option>
                              <option value="design">Landscape Design</option>
                              <option value="installation">Landscape Installation</option>
                              <option value="maintenance">Maintenance</option>
                              <option value="snow">Snow Removal</option>
                              <option value="other">Other</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="propertyType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Property Type</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                            >
                              <option value="">Select property type</option>
                              <option value="residential">Residential</option>
                              <option value="commercial">Commercial</option>
                              <option value="estate">Estate</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Project Budget Range</FormLabel>
                          <FormControl>
                            <select
                              {...field}
                              className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                            >
                              <option value="">Select budget range</option>
                              <option value="below-10k">Below $10,000</option>
                              <option value="10k-25k">$10,000 - $25,000</option>
                              <option value="25k-50k">$25,000 - $50,000</option>
                              <option value="50k-100k">$50,000 - $100,000</option>
                              <option value="above-100k">Above $100,000</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="timeline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Timeline</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                          >
                            <option value="">Select preferred timeline</option>
                            <option value="immediate">As soon as possible</option>
                            <option value="1-month">Within 1 month</option>
                            <option value="3-months">Within 3 months</option>
                            <option value="6-months">Within 6 months</option>
                            <option value="flexible">Flexible</option>
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Details *</FormLabel>
                        <FormControl>
                          <textarea
                            {...field}
                            rows={5}
                            className="w-full px-4 py-3 rounded-md border border-pkstone-200 focus:border-pkearth-500 focus:ring-2 focus:ring-pkearth-200 transition-all duration-300 outline-none"
                            placeholder="Tell us about your project, goals, and specific requirements..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="text-sm text-pkstone-600 mb-4">
                    <p>* Required fields</p>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                      "w-full md:w-auto bg-pkearth-700 hover:bg-pkearth-800 text-white font-medium py-3 px-8 rounded-md inline-flex items-center justify-center transition-all duration-300",
                      isSubmitting && "opacity-70 cursor-not-allowed"
                    )}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
      
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <DialogTitle className="text-xl font-medium text-center">Request Received</DialogTitle>
            <DialogDescription className="text-center pt-2">
              <p className="mb-2">Thank you for contacting {businessInfo.name}.</p>
              <p>Our team will review your inquiry and get back to you within 24-48 hours during business days.</p>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactForm;
